// Built-in relance type card — used for catchup / project / lastShot.
//
// Re-implements `RTypeCard` from `prototypes_and_docs/ls-relance.jsx:159-252`
// over DS primitives. The "editable" variant (custom type) lives in
// `CustomTypeEditor` so this file stays focused on the read-only header
// path.

import { useT } from '@barel/i18n';
import { Button, FieldAssembly, IconButton, Segmented, Switch, Textarea } from '@barel/ui';

export type BuiltInTypeKind = 'catchup' | 'project' | 'lastShot';

export interface BuiltInTypeConfig {
  enabled: boolean;
  delayDays: number;
  maxAttempts: 1 | 2;
  hint: string;
}

export interface RelanceTypeCardProps {
  kind: BuiltInTypeKind;
  config: BuiltInTypeConfig;
  onChange: (next: BuiltInTypeConfig) => void;
  disabled?: boolean;
}

const ICONS: Record<BuiltInTypeKind, string> = {
  catchup: '👋',
  project: '🎯',
  lastShot: '🚪',
};

export function RelanceTypeCard({ kind, config, onChange, disabled }: RelanceTypeCardProps) {
  const t = useT('relances');

  const titleKey = `types.${kind}Title` as const;
  const shortKey = `types.${kind}Short` as const;
  const whenKey = `types.${kind}When` as const;
  const placeholderKey = `types.${kind}Hint` as const;

  const set = (patch: Partial<BuiltInTypeConfig>): void => {
    onChange({ ...config, ...patch });
  };

  return (
    <div
      data-component="RelanceTypeCard"
      data-kind={kind}
      className="rounded-xl border border-default bg-bg-surface px-4 py-3"
    >
      <div className="flex items-start gap-3">
        <div
          aria-hidden
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-default bg-bg-elevated text-base"
        >
          {ICONS[kind]}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-primary">{t(titleKey)}</div>
          <p className="text-xs text-tertiary">{t(shortKey)}</p>
        </div>
        <Switch
          aria-label={t(titleKey)}
          checked={config.enabled}
          onCheckedChange={(next: boolean) => set({ enabled: next })}
          disabled={disabled}
          data-testid={`relance-type-${kind}-switch`}
        />
      </div>

      {config.enabled ? (
        <div
          className="mt-3 flex flex-col gap-3 border-t border-subtle pt-3"
          data-testid={`relance-type-${kind}-body`}
        >
          <div>
            <div className="text-2xs font-semibold uppercase tracking-wider text-secondary">
              {t('types.whenLabel')}
            </div>
            <p className="mt-1 text-xs text-primary leading-relaxed">{t(whenKey)}</p>
          </div>

          <div className="flex flex-wrap gap-4">
            <FieldAssembly label={t('types.delayLabel')}>
              <DayStepper
                value={config.delayDays}
                onChange={(v) => set({ delayDays: v })}
                disabled={disabled}
                ariaLabel={t('types.delayLabel')}
                renderValue={(v) => t('types.delayValue', { days: v })}
                testId={`relance-type-${kind}-delay`}
              />
            </FieldAssembly>
            <FieldAssembly label={t('types.maxAttemptsLabel')}>
              <Segmented<'1' | '2'>
                value={String(config.maxAttempts) as '1' | '2'}
                onValueChange={(next) => set({ maxAttempts: Number(next) as 1 | 2 })}
                options={[
                  { value: '1', label: t('types.maxAttempts1') },
                  { value: '2', label: t('types.maxAttempts2') },
                ]}
                aria-label={t('types.maxAttemptsLabel')}
                size="sm"
                disabled={disabled}
              />
            </FieldAssembly>
          </div>

          <FieldAssembly label={t('types.aiHintLabel')}>
            <Textarea
              value={config.hint}
              onChange={(event) => set({ hint: event.target.value })}
              placeholder={t(placeholderKey)}
              rows={3}
              disabled={disabled}
              data-testid={`relance-type-${kind}-hint`}
            />
          </FieldAssembly>
        </div>
      ) : null}
    </div>
  );
}

interface DayStepperProps {
  value: number;
  onChange: (next: number) => void;
  disabled?: boolean;
  ariaLabel: string;
  renderValue: (n: number) => string;
  testId?: string;
}

function DayStepper({
  value,
  onChange,
  disabled,
  ariaLabel,
  renderValue,
  testId,
}: DayStepperProps) {
  const min = 1;
  const max = 90;
  const v = Math.max(min, Math.min(max, value || min));
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="inline-flex items-center overflow-hidden rounded-md border border-strong bg-bg-elevated"
      data-testid={testId}
    >
      <IconButton
        aria-label={`${ariaLabel} —`}
        size="sm"
        variant="ghost"
        onClick={() => onChange(Math.max(min, v - 1))}
        disabled={disabled || v <= min}
        type="button"
        icon={<span aria-hidden>−</span>}
      />
      <div
        className="min-w-[64px] border-x border-default px-2 text-center text-xs font-semibold text-primary"
        aria-live="polite"
      >
        {renderValue(v)}
      </div>
      <IconButton
        aria-label={`${ariaLabel} +`}
        size="sm"
        variant="ghost"
        onClick={() => onChange(Math.min(max, v + 1))}
        disabled={disabled || v >= max}
        type="button"
        icon={<span aria-hidden>+</span>}
      />
    </div>
  );
}

export interface AddCustomTypeButtonProps {
  onClick: () => void;
  disabled?: boolean;
}

export function AddCustomTypeButton({ onClick, disabled }: AddCustomTypeButtonProps) {
  const t = useT('relances');
  return (
    <Button
      variant="ghost"
      size="md"
      onClick={onClick}
      disabled={disabled}
      type="button"
      fullWidth
      className="border border-dashed border-strong"
      data-testid="relance-add-custom-type"
    >
      {t('custom.addCta')}
    </Button>
  );
}
